/**
 * Redux Slice for Search State
 *
 * Manages search UI state, filters, and user preferences.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import type {
  SearchQuery,
  SearchResults,
  SearchFilter,
  SearchHistoryItem,
  SearchSyncStatus,
} from '../types';

export interface SearchState {
  /** Current search query */
  currentQuery: SearchQuery | null;
  /** Current search results */
  currentResults: SearchResults | null;
  /** Search in progress */
  isSearching: boolean;
  /** Search error */
  searchError: string | null;
  /** Active filters */
  activeFilters: SearchFilter[];
  /** Search panel visibility */
  searchPanelOpen: boolean;
  /** Advanced search mode */
  advancedMode: boolean;
  /** Recent searches */
  recentSearches: SearchHistoryItem[];
  /** Saved searches */
  savedSearches: SearchFilter[];
  /** Search suggestions */
  suggestions: string[];
  /** Show search history */
  showHistory: boolean;
  /** Sync status for account users */
  syncStatus: SearchSyncStatus;
  /** UI preferences */
  ui: {
    /** Results view mode */
    resultView: 'list' | 'grid' | 'compact';
    /** Show snippets */
    showSnippets: boolean;
    /** Highlight matches */
    highlightMatches: boolean;
    /** Results per page */
    resultsPerPage: number;
    /** Sort order */
    sortBy: 'relevance' | 'date' | 'title';
    /** Sort direction */
    sortDirection: 'asc' | 'desc';
  };
}

const initialState: SearchState = {
  currentQuery: null,
  currentResults: null,
  isSearching: false,
  searchError: null,
  activeFilters: [],
  searchPanelOpen: false,
  advancedMode: false,
  recentSearches: [],
  savedSearches: [],
  suggestions: [],
  showHistory: false,
  syncStatus: {
    isEnabled: false,
    pendingChanges: 0,
    status: 'idle',
  },
  ui: {
    resultView: 'list',
    showSnippets: true,
    highlightMatches: true,
    resultsPerPage: 20,
    sortBy: 'relevance',
    sortDirection: 'desc',
  },
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    // Set current search query
    setSearchQuery: (state, action: PayloadAction<SearchQuery>) => {
      state.currentQuery = action.payload;
      state.searchError = null;
    },

    // Clear search query
    clearSearchQuery: (state) => {
      state.currentQuery = null;
      state.currentResults = null;
      state.searchError = null;
      state.suggestions = [];
    },

    // Set search results
    setSearchResults: (state, action: PayloadAction<SearchResults>) => {
      state.currentResults = action.payload;
      state.isSearching = false;
      state.searchError = null;
    },

    // Set searching state
    setSearching: (state, action: PayloadAction<boolean>) => {
      state.isSearching = action.payload;
      if (action.payload) {
        state.searchError = null;
      }
    },

    // Set search error
    setSearchError: (state, action: PayloadAction<string>) => {
      state.searchError = action.payload;
      state.isSearching = false;
    },

    // Add filter
    addFilter: (state, action: PayloadAction<SearchFilter>) => {
      const exists = state.activeFilters.some(
        (f) => f.id === action.payload.id
      );
      if (!exists) {
        state.activeFilters.push(action.payload);
      }
    },

    // Remove filter
    removeFilter: (state, action: PayloadAction<string>) => {
      state.activeFilters = state.activeFilters.filter(
        (f) => f.id !== action.payload
      );
    },

    // Clear all filters
    clearFilters: (state) => {
      state.activeFilters = [];
    },

    // Toggle search panel
    toggleSearchPanel: (state) => {
      state.searchPanelOpen = !state.searchPanelOpen;
    },

    // Set search panel visibility
    setSearchPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.searchPanelOpen = action.payload;
    },

    // Toggle advanced mode
    toggleAdvancedMode: (state) => {
      state.advancedMode = !state.advancedMode;
    },

    // Add to recent searches
    addToRecentSearches: (state, action: PayloadAction<SearchHistoryItem>) => {
      // Remove duplicate if exists
      state.recentSearches = state.recentSearches.filter(
        (s) => s.query.query !== action.payload.query.query
      );
      // Add to beginning
      state.recentSearches.unshift(action.payload);
      // Keep only last 10
      state.recentSearches = state.recentSearches.slice(0, 10);
    },

    // Clear recent searches
    clearRecentSearches: (state) => {
      state.recentSearches = [];
    },

    // Save search
    saveSearch: (state, action: PayloadAction<SearchFilter>) => {
      const exists = state.savedSearches.some(
        (s) => s.id === action.payload.id
      );
      if (!exists) {
        state.savedSearches.push(action.payload);
      }
    },

    // Remove saved search
    removeSavedSearch: (state, action: PayloadAction<string>) => {
      state.savedSearches = state.savedSearches.filter(
        (s) => s.id !== action.payload
      );
    },

    // Set suggestions
    setSuggestions: (state, action: PayloadAction<string[]>) => {
      state.suggestions = action.payload;
    },

    // Toggle history visibility
    toggleHistory: (state) => {
      state.showHistory = !state.showHistory;
    },

    // Update sync status
    updateSyncStatus: (
      state,
      action: PayloadAction<Partial<SearchSyncStatus>>
    ) => {
      state.syncStatus = {
        ...state.syncStatus,
        ...action.payload,
      };
    },

    // Set result view mode
    setResultView: (
      state,
      action: PayloadAction<'list' | 'grid' | 'compact'>
    ) => {
      state.ui.resultView = action.payload;
    },

    // Toggle snippets
    toggleSnippets: (state) => {
      state.ui.showSnippets = !state.ui.showSnippets;
    },

    // Toggle highlight
    toggleHighlight: (state) => {
      state.ui.highlightMatches = !state.ui.highlightMatches;
    },

    // Set results per page
    setResultsPerPage: (state, action: PayloadAction<number>) => {
      state.ui.resultsPerPage = action.payload;
    },

    // Set sort order
    setSortOrder: (
      state,
      action: PayloadAction<{
        sortBy: 'relevance' | 'date' | 'title';
        sortDirection: 'asc' | 'desc';
      }>
    ) => {
      state.ui.sortBy = action.payload.sortBy;
      state.ui.sortDirection = action.payload.sortDirection;
    },

    // Reset search state
    resetSearchState: () => initialState,
  },
});

// Export actions
export const {
  setSearchQuery,
  clearSearchQuery,
  setSearchResults,
  setSearching,
  setSearchError,
  addFilter,
  removeFilter,
  clearFilters,
  toggleSearchPanel,
  setSearchPanelOpen,
  toggleAdvancedMode,
  addToRecentSearches,
  clearRecentSearches,
  saveSearch,
  removeSavedSearch,
  setSuggestions,
  toggleHistory,
  updateSyncStatus,
  setResultView,
  toggleSnippets,
  toggleHighlight,
  setResultsPerPage,
  setSortOrder,
  resetSearchState,
} = searchSlice.actions;

// Selectors
export const selectCurrentQuery = (state: RootState) =>
  state.search.currentQuery;

export const selectCurrentResults = (state: RootState) =>
  state.search.currentResults;

export const selectIsSearching = (state: RootState) => state.search.isSearching;

export const selectSearchError = (state: RootState) => state.search.searchError;

export const selectActiveFilters = (state: RootState) =>
  state.search.activeFilters;

export const selectSearchPanelOpen = (state: RootState) =>
  state.search.searchPanelOpen;

export const selectAdvancedMode = (state: RootState) =>
  state.search.advancedMode;

export const selectRecentSearches = (state: RootState) =>
  state.search.recentSearches;

export const selectSavedSearches = (state: RootState) =>
  state.search.savedSearches;

export const selectSuggestions = (state: RootState) => state.search.suggestions;

export const selectShowHistory = (state: RootState) => state.search.showHistory;

export const selectSyncStatus = (state: RootState) => state.search.syncStatus;

export const selectSearchUI = (state: RootState) => state.search.ui;

export const selectResultView = (state: RootState) =>
  state.search.ui.resultView;

export const selectShowSnippets = (state: RootState) =>
  state.search.ui.showSnippets;

export const selectHighlightMatches = (state: RootState) =>
  state.search.ui.highlightMatches;

export const selectResultsPerPage = (state: RootState) =>
  state.search.ui.resultsPerPage;

export const selectSortOrder = (state: RootState) => ({
  sortBy: state.search.ui.sortBy,
  sortDirection: state.search.ui.sortDirection,
});

export default searchSlice.reducer;
